import { Link } from 'react-router'

import { Banner } from '@/components/ui/banner'
import { filePath, type FirstAsk } from '@/models/vault'
import { useVault } from './VaultContext'

const MAX_ITEMS = 3

/** Onboarding ends here: the first files to send, most urgent first, each a link into the vault. */
export function FirstAsks({
  asks,
  emailed,
  contacts,
  onDismiss,
}: {
  asks: FirstAsk[]
  /** Whether confirming sent a digest; a re-save sends nothing the owners already have. */
  emailed: boolean
  /** The addresses the digest actually went to, which the carrier may have changed. */
  contacts: string[]
  onDismiss: () => void
}) {
  const { href } = useVault()
  return (
    <Banner variant="info" onDismiss={onDismiss}>
      <div className="flex flex-col gap-2">
        <p>
          <span className="font-semibold">Scope confirmed.</span>{' '}
          {asks.length > 0 ? 'Here is what to send first:' : 'Everything required is on file.'}
        </p>
        {asks.length > 0 && (
          <ul className="list-disc pl-4">
            {asks.map((ask) => {
              const shown = ask.items.slice(0, MAX_ITEMS).join(', ')
              const more = ask.items.length - MAX_ITEMS
              const to = href(filePath(ask.fileKind, ask.subjectId ?? undefined))
              return (
                <li key={`${ask.fileKind}-${ask.subjectId ?? 'carrier'}`}>
                  <Link to={to} className="font-medium underline-offset-2 hover:underline">
                    {ask.name}
                  </Link>
                  : {shown}
                  {more > 0 ? ` and ${more} more` : ''}
                </li>
              )
            })}
          </ul>
        )}
        {contacts.length > 0 && emailed && (
          <p>
            We’ve emailed this list to {contacts.join(', ')}. Reply with the documents attached and we file
            them.
          </p>
        )}
        {contacts.length > 0 && !emailed && asks.length > 0 && (
          <p>
            {contacts.join(', ')} already have this list from the last email, so nothing new went out. The
            weekly follow-up covers whatever is still open.
          </p>
        )}
      </div>
    </Banner>
  )
}
